import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { 
  LayoutDashboard, 
  Building2, 
  ScrollText, 
  QrCode, 
  Users, 
  ShoppingCart,
  Settings,
  LogOut,
  Briefcase,
  UserCog,
  User
} from 'lucide-react';
import { cn } from '@/lib/utils';

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout, isAdmin, isAgente } = useAuth();
  const location = useLocation();

  const adminLinks = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/aziende', icon: Building2, label: 'Aziende' },
    { to: '/elogi', icon: ScrollText, label: 'Elogi' },
    { to: '/qrcode', icon: QrCode, label: 'QR Code' },
    { to: '/agenti', icon: Users, label: 'Agenti' },
    { to: '/utenti', icon: UserCog, label: 'Utenti' },
    { to: '/ordini', icon: ShoppingCart, label: 'Ordini' },
    { to: '/impostazioni', icon: Settings, label: 'Impostazioni' },
  ];

  const agenteLinks = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/aziende', icon: Building2, label: 'Le tue Aziende' },
    { to: '/elogi', icon: ScrollText, label: 'Elogi Demo' },
    { to: '/commissioni', icon: Briefcase, label: 'Commissioni' },
  ];

  const aziendaLinks = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/elogi', icon: ScrollText, label: 'Elogi' },
    { to: '/qrcode', icon: QrCode, label: 'QR Code' },
    { to: '/ordini', icon: ShoppingCart, label: 'Ordini' },
    { to: '/profilo', icon: User, label: 'Profilo' },
  ];

  const links = isAdmin() ? adminLinks : isAgente() ? agenteLinks : aziendaLinks;

  const handleLinkClick = () => {
    if (onClose) onClose();
  };

  return (
    <aside 
      className={cn(
        "fixed left-0 top-0 z-50 h-[100dvh] w-64 bg-slate-900 text-white flex flex-col transition-transform duration-300 ease-in-out",
        "lg:translate-x-0 lg:h-screen",
        isOpen ? "translate-x-0" : "-translate-x-full"
      )}
      data-testid="sidebar"
    >
      {/* Logo - Hidden on mobile (shown in header) */}
      <div className="hidden lg:block p-6 border-b border-slate-800">
        <h1 className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
          RememberMe
        </h1>
        <p className="text-xs text-slate-400 mt-1">Live Memorial System</p>
      </div>

      {/* Mobile header spacer */}
      <div className="lg:hidden h-16 flex-shrink-0" />

      {/* Navigation - scrollable */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto min-h-0">
        {links.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            onClick={handleLinkClick}
            data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={({ isActive }) => cn(
              'flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-sm',
              isActive || location.pathname === link.to
                ? 'bg-sky-500/20 text-sky-400'
                : 'text-slate-300 hover:bg-slate-800 hover:text-white'
            )}
          >
            <link.icon className="h-5 w-5" />
            {link.label}
          </NavLink>
        ))}
      </nav>

      {/* User Info - always visible at bottom */}
      <div className="p-4 border-t border-slate-800 flex-shrink-0 bg-slate-900 safe-area-bottom" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
            <span className="text-sky-400 font-medium">
              {user?.nome?.[0]}{user?.cognome?.[0]}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.nome} {user?.cognome}</p>
            <p className="text-xs text-slate-400 capitalize">{user?.ruolo}</p>
          </div>
        </div>
        <button
          onClick={() => { logout(); if (onClose) onClose(); }}
          data-testid="logout-btn"
          className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-red-400 hover:text-white hover:bg-red-600 rounded-lg transition-colors"
        >
          <LogOut className="h-4 w-4" />
          Esci
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
